import {
  IdentityAdministrationContractError,
  type OrganizationMembershipResponse,
  type RelationshipStatus,
} from "@kontextwerk/control-web-client"

/**
 * Membership of the KB roster. `email` is only filled for callers that may manage memberships;
 * older servers omit it. It is a display fact and never an authorization input.
 */
export type KehrblattMembership = OrganizationMembershipResponse &
  Readonly<{ email: string | null }>
export type KehrblattMembershipPage = Readonly<{
  items: readonly KehrblattMembership[]
  next_after_id: string | null
}>
export type KehrblattMembershipChange = Readonly<{
  membership: KehrblattMembership
  changed: boolean
}>

const UUID = /^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/i
const MEMBERSHIP_FIELDS = [
  "id",
  "user_id",
  "organization_id",
  "role_id",
  "status",
  "revision",
  "email",
] as const

export const invalid = (): never => {
  throw new IdentityAdministrationContractError()
}

export const fields = (value: unknown, keys: readonly string[]): Record<string, unknown> => {
  if (
    !value ||
    typeof value !== "object" ||
    Array.isArray(value) ||
    Object.keys(value).some((key) => !keys.includes(key))
  )
    return invalid()

  return value as Record<string, unknown>
}

export const uuid = (value: unknown): string =>
  typeof value === "string" && UUID.test(value) ? value.toLowerCase() : invalid()

export const revision = (value: unknown): number =>
  typeof value === "number" && Number.isSafeInteger(value) && value >= 1 ? value : invalid()

export const relationshipStatus = (value: unknown): RelationshipStatus =>
  value === "active" || value === "suspended" ? value : invalid()

/** Display-only: an absent, null or implausible address is shown as unknown, never guessed. */
const displayEmail = (value: unknown): string | null =>
  typeof value === "string" && value.length <= 320 && /^[^\s@]+@[^\s@]+$/.test(value) ? value : null

export const membership = (
  value: unknown,
  organizationId: string,
  id?: string,
): KehrblattMembership => {
  const data = fields(value, MEMBERSHIP_FIELDS)
  const parsed: KehrblattMembership = Object.freeze({
    id: uuid(data.id),
    user_id: uuid(data.user_id),
    organization_id: uuid(data.organization_id),
    role_id: uuid(data.role_id),
    status: relationshipStatus(data.status),
    revision: revision(data.revision),
    email: displayEmail(data.email),
  })

  if (parsed.organization_id !== organizationId || (id !== undefined && parsed.id !== id))
    return invalid()

  return parsed
}

export const membershipPage = (value: unknown, organizationId: string): KehrblattMembershipPage => {
  const data = fields(value, ["items", "next_after_id"])

  if (!Array.isArray(data.items) || data.items.length > 100) return invalid()

  const items = Object.freeze(data.items.map((item) => membership(item, organizationId)))

  if (new Set(items.map((item) => item.id)).size !== items.length) return invalid()

  return Object.freeze({
    items,
    next_after_id: data.next_after_id === null ? null : uuid(data.next_after_id),
  })
}

export const membershipChange = (
  value: unknown,
  organizationId: string,
  id: string,
): KehrblattMembershipChange => {
  const data = fields(value, ["membership", "changed"])

  if (typeof data.changed !== "boolean") return invalid()

  return Object.freeze({
    membership: membership(data.membership, organizationId, id),
    changed: data.changed,
  })
}
